import axiosInstance from '../../config/axios.config';

/**
 * ProfileService - Agent Profile API Service
 * Handles all profile-related API calls for LeadFlow V2.0 Agent Profiles
 */
const ProfileService = {
    /**
     * Get current user's profile
     * @returns {Promise} Profile data
     */
    getMyProfile: async () => {
        const response = await axiosInstance.get('/leadflow/profile');
        return response.data;
    },

    /**
     * Get user's main profile details (from tenant user-details)
     * Used to import data into social profile
     * @returns {Promise} User profile data with basic_info, establishment_info, etc.
     */
    getUserProfileDetails: async () => {
        const response = await axiosInstance.get('/user-details/profile');
        return response.data;
    },

    /**
     * Get another user's profile by profile ID
     * @param {string|number} profileId - The profile ID to fetch
     * @returns {Promise} Profile data with connection status
     */
    getProfileById: async (profileId) => {
        const response = await axiosInstance.get(`/leadflow/profiles/${profileId}`);
        return response.data;
    },

    /**
     * List mutual connections between viewer and target profile (paginated, searchable).
     * @param {string|number} profileId
     * @param {{ q?: string, page?: number, per_page?: number }} [params]
     */
    getMutualConnections: async (profileId, params = {}) => {
        const response = await axiosInstance.get(
            `/leadflow/profiles/${profileId}/mutuals`,
            { params }
        );
        return response.data;
    },

    /**
     * Create or update profile
     * @param {Object} profileData - Profile data
     * @returns {Promise} Updated profile
     */
    saveProfile: async (profileData) => {
        const response = await axiosInstance.put('/leadflow/profile', profileData);
        return response.data;
    },

    /**
     * Create new profile (alias for saveProfile for backward compatibility)
     * @param {Object} profileData - Profile data
     * @returns {Promise} Created profile
     */
    createProfile: async (profileData) => {
        const response = await axiosInstance.put('/leadflow/profile', profileData);
        return response.data;
    },

    /**
     * Upload profile photo
     * @param {File} file - Image file
     * @returns {Promise} Uploaded photo URL
     */
    uploadProfilePhoto: async (file) => {
        const formData = new FormData();
        formData.append('photo', file);  // Backend expects 'photo' field

        const response = await axiosInstance.post('/leadflow/profile/photo', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    /**
     * Upload cover photo
     * @param {File} file - Image file
     * @returns {Promise} Uploaded photo URL
     */
    uploadCoverPhoto: async (file) => {
        const formData = new FormData();
        formData.append('photo', file);  // Backend expects 'photo' field

        const response = await axiosInstance.post('/leadflow/profile/cover', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    /**
     * Delete profile photo
     * @returns {Promise} Deletion confirmation
     */
    deleteProfilePhoto: async () => {
        const response = await axiosInstance.delete('/leadflow/profile/photo');
        return response.data;
    },

    /**
     * Delete cover photo
     * @returns {Promise} Deletion confirmation
     */
    deleteCoverPhoto: async () => {
        const response = await axiosInstance.delete('/leadflow/profile/cover');
        return response.data;
    },

    /**
     * Get merged list of company names for "Companies I Work With" MultiSelect.
     * Sources: central_company_lists, central_general_company_lists, tenant companies.
     * @returns {Promise<string[]>} Sorted unique company names
     */
    getLenderCompanies: async () => {
        const response = await axiosInstance.get('/leadflow/profile/lender-companies');
        return response.data;
    },

    /**
     * Lending Network privacy — how far this member's profile and posts reach.
     *
     * Returns the member's preference, the effective level after their company
     * policy and the tenant ceiling are applied, and `can_edit` — false for
     * company employees and agents, whose company admin owns these settings.
     */
    getPrivacy: async () => {
        const response = await axiosInstance.get('/leadflow/profile/privacy');
        return response.data;
    },

    /**
     * Direct users only — the API refuses this for company members.
     * @param {'public'|'tenant'|'private'} profileVisibility
     * @param {'public'|'tenant'|'private'} postVisibility
     */
    updatePrivacy: async (profileVisibility, postVisibility) => {
        const response = await axiosInstance.put('/leadflow/profile/privacy', {
            profile_visibility: profileVisibility,
            post_visibility: postVisibility,
        });
        return response.data;
    },

    /**
     * Get profile completion status
     * @returns {Promise} Completion percentage and missing fields
     */
    getProfileCompletion: async () => {
        const response = await axiosInstance.get('/leadflow/profile/completion');
        return response.data;
    },

    // ===== Work Experience APIs =====

    /**
     * Get all work experiences
     * @returns {Promise} Array of work experiences
     */
    getWorkExperiences: async () => {
        const response = await axiosInstance.get('/leadflow/profile/work-experiences');
        return response.data;
    },

    /**
     * Add work experience
     * @param {Object} workData - Work experience data
     * @returns {Promise} Created work experience
     */
    addWorkExperience: async (workData) => {
        const response = await axiosInstance.post('/leadflow/profile/work-experiences', workData);
        return response.data;
    },

    /**
     * Update work experience
     * @param {number} id - Work experience ID
     * @param {Object} workData - Updated data
     * @returns {Promise} Updated work experience
     */
    updateWorkExperience: async (id, workData) => {
        const response = await axiosInstance.put(`/leadflow/profile/work-experiences/${id}`, workData);
        return response.data;
    },

    /**
     * Delete work experience
     * @param {number} id - Work experience ID
     * @returns {Promise} Deletion confirmation
     */
    deleteWorkExperience: async (id) => {
        const response = await axiosInstance.delete(`/leadflow/profile/work-experiences/${id}`);
        return response.data;
    },

    // ===== Education APIs =====

    /**
     * Get all education records
     * @returns {Promise} Array of education records
     */
    getEducation: async () => {
        const response = await axiosInstance.get('/leadflow/profile/education');
        return response.data;
    },

    /**
     * Add education record
     * @param {Object} eduData - Education data
     * @returns {Promise} Created education record
     */
    addEducation: async (eduData) => {
        const response = await axiosInstance.post('/leadflow/profile/education', eduData);
        return response.data;
    },

    /**
     * Update education record
     * @param {number} id - Education ID
     * @param {Object} eduData - Updated data
     * @returns {Promise} Updated education record
     */
    updateEducation: async (id, eduData) => {
        const response = await axiosInstance.put(`/leadflow/profile/education/${id}`, eduData);
        return response.data;
    },

    /**
     * Delete education record
     * @param {number} id - Education ID
     * @returns {Promise} Deletion confirmation
     */
    deleteEducation: async (id) => {
        const response = await axiosInstance.delete(`/leadflow/profile/education/${id}`);
        return response.data;
    },

    // ===== Certifications APIs =====

    /**
     * Get all certifications
     * @returns {Promise} Array of certifications
     */
    getCertifications: async () => {
        const response = await axiosInstance.get('/leadflow/profile/certifications');
        return response.data;
    },

    /**
     * Add certification
     * @param {Object} certData - Certification data
     * @returns {Promise} Created certification
     */
    addCertification: async (certData) => {
        const response = await axiosInstance.post('/leadflow/profile/certifications', certData);
        return response.data;
    },

    /**
     * Update certification
     * @param {number} id - Certification ID
     * @param {Object} certData - Updated data
     * @returns {Promise} Updated certification
     */
    updateCertification: async (id, certData) => {
        const response = await axiosInstance.put(`/leadflow/profile/certifications/${id}`, certData);
        return response.data;
    },

    /**
     * Delete certification
     * @param {number} id - Certification ID
     * @returns {Promise} Deletion confirmation
     */
    deleteCertification: async (id) => {
        const response = await axiosInstance.delete(`/leadflow/profile/certifications/${id}`);
        return response.data;
    },

    // ===== Skills APIs =====

    /**
     * Get all skills
     * @returns {Promise} Array of skills
     */
    getSkills: async () => {
        const response = await axiosInstance.get('/leadflow/profile/skills');
        return response.data;
    },

    /**
     * Add skill
     * @param {Object} skillData - Skill data
     * @returns {Promise} Created skill
     */
    addSkill: async (skillData) => {
        const response = await axiosInstance.post('/leadflow/profile/skills', skillData);
        return response.data;
    },

    /**
     * Update skill
     * @param {number} id - Skill ID
     * @param {Object} skillData - Updated data
     * @returns {Promise} Updated skill
     */
    updateSkill: async (id, skillData) => {
        const response = await axiosInstance.put(`/leadflow/profile/skills/${id}`, skillData);
        return response.data;
    },

    /**
     * Delete skill
     * @param {number} id - Skill ID
     * @returns {Promise} Deletion confirmation
     */
    deleteSkill: async (id) => {
        const response = await axiosInstance.delete(`/leadflow/profile/skills/${id}`);
        return response.data;
    },

    // ===== Languages APIs =====

    /**
     * Get all languages
     * @returns {Promise} Array of languages
     */
    getLanguages: async () => {
        const response = await axiosInstance.get('/leadflow/profile/languages');
        return response.data;
    },

    /**
     * Add language
     * @param {Object} langData - Language data
     * @returns {Promise} Created language
     */
    addLanguage: async (langData) => {
        const response = await axiosInstance.post('/leadflow/profile/languages', langData);
        return response.data;
    },

    /**
     * Update language
     * @param {number} id - Language ID
     * @param {Object} langData - Updated data
     * @returns {Promise} Updated language
     */
    updateLanguage: async (id, langData) => {
        const response = await axiosInstance.put(`/leadflow/profile/languages/${id}`, langData);
        return response.data;
    },

    /**
     * Delete language
     * @param {number} id - Language ID
     * @returns {Promise} Deletion confirmation
     */
    deleteLanguage: async (id) => {
        const response = await axiosInstance.delete(`/leadflow/profile/languages/${id}`);
        return response.data;
    },

};

export default ProfileService;
